import { cohortUtilizationSnapshotQuery } from "@/api/reporting";
import { ChartBox } from "@/components/charts/ChartBox";
import { MonthPicker } from "@/components/forms/MonthPicker";
import { SupporterIdInput } from "@/components/forms/SupporterIdInput";
import { Card } from "@/components/ui/Card";
import { ALL_COUNTRIES, ALL_TOKEN_CURRENCIES, COUNTRY_DATA } from "@/constants/countries";
import { TokenCurrencyCode, tokensToMoney } from "@/modules/money-fns";
import { dateToIsoMonthString, isoMonthStringToDate } from "@/pages/supporter/allowance-reports/utils";
import { toDateOnly } from "@common/utils/date";
import { formatDollars, formatWholeNumber } from "@common/utils/i18n";
import { Box, Checkbox, Flex, Label, Select, Text } from "@givenwell/components";
import { CohortSummary } from "@givenwell/management-api";
import { barX, defineChart, ruleX, text } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { custom, fallback, object, optional, pipe, string } from "valibot";
import { UtilisationHistoryByCohort } from "./UtilisationHistoryByCohort";

const today = new Date();
const endOfThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
const twelveMonthsAgo = new Date(today.getFullYear() - 1, today.getMonth() + 2, 0);

const isMonthString = () => custom<string>(input => (typeof input === "string" ? /^\d{4}-\d{2}$/.test(input) : false));

export const Route = createFileRoute("/dashboard/utilisation")({
  component: Page,
  validateSearch: object({
    country: fallback(string(), ALL_COUNTRIES),
    from: fallback(
      pipe(optional(string(), dateToIsoMonthString(twelveMonthsAgo)), isMonthString()),
      dateToIsoMonthString(twelveMonthsAgo),
    ),
    to: fallback(
      pipe(optional(string(), dateToIsoMonthString(endOfThisMonth)), isMonthString()),
      dateToIsoMonthString(endOfThisMonth),
    ),
  }),
  beforeLoad: () => ({
    breadcrumb: "Utilisation",
  }),
});

const BAR_HEIGHT = 30;
const BAR_GAP = 5;
const AXIS_HEIGHT = 30;

function Page() {
  const nav = Route.useNavigate();
  const { from: rawFrom, to: rawTo, country: countryCode } = Route.useSearch();
  const from = isoMonthStringToDate(rawFrom);
  const to = isoMonthStringToDate(rawTo);
  const [normalised, setNormalised] = useState(false);
  const [supporterId, setSupporterId] = useState<string>("");
  const [excludeGivenwell, setExcludeGivenwell] = useState(true);

  // Country data. The "all countries" view combines every token currency, so it has no
  // single display currency — charts fall back to raw token counts instead of dollars.
  const { tokenCurrencies, tokenCurrency, currency } = useMemo(() => {
    if (countryCode === ALL_COUNTRIES) {
      return { tokenCurrencies: ALL_TOKEN_CURRENCIES, tokenCurrency: undefined, currency: undefined };
    }
    const countryData = COUNTRY_DATA[countryCode];
    if (!countryData) {
      throw new Error(`Unknown country code: ${countryCode}`);
    }
    return {
      tokenCurrencies: [countryData.tokenCurrency],
      tokenCurrency: countryData.tokenCurrency,
      currency: countryData.currency,
    };
  }, [countryCode]);

  // Cohort data..
  const today = toDateOnly(new Date());
  const { data } = useQuery(
    cohortUtilizationSnapshotQuery(
      today,
      `${rawFrom}-01`,
      `${rawTo}-01`,
      !excludeGivenwell,
      tokenCurrencies,
      supporterId || undefined,
    ),
  );
  const cohorts = useMemo(() => {
    const unsortedCohorts = data?.cohorts || [];
    return unsortedCohorts.sort((a, b) => a.cohort_date.localeCompare(b.cohort_date));
  }, [data]);

  const countries = [
    { value: ALL_COUNTRIES, label: "All countries" },
    ...Object.entries(COUNTRY_DATA).map(([code, country]) => ({ value: code, label: country.name })),
  ];

  return (
    <Box css={{ height: "100%", overflow: "auto" }}>
      <Flex css={{ gap: 16, marginBottom: 8, height: 52, items: "center" }}>
        <Select
          css={{ width: 200 }}
          size="sm"
          value={countryCode}
          onValueChange={value => {
            nav({
              search: {
                country: value,
                from: dateToIsoMonthString(from),
                to: dateToIsoMonthString(to),
              },
            });
          }}
        >
          {countries.map(country => (
            <option key={country.value} value={country.value}>
              {country.label}
            </option>
          ))}
        </Select>
        <CohortRangePicker
          from={from}
          to={to}
          onChange={(from, to) => {
            nav({
              search: {
                country: countryCode,
                from: dateToIsoMonthString(from),
                to: dateToIsoMonthString(to),
              },
            });
          }}
        />
        <Label css={{ d: "flex", gap: 8, items: "center" }}>
          <Checkbox checked={normalised} onCheckedChange={setNormalised} />
          <Text> Normalise %</Text>
        </Label>
        <Label css={{ d: "flex", gap: 8, items: "center" }}>
          <Checkbox checked={excludeGivenwell} onCheckedChange={setExcludeGivenwell} />
          <Text> Exclude Givenwell</Text>
        </Label>
        <SupporterIdInput size="sm" value={supporterId} onValueChange={setSupporterId} />
      </Flex>
      <Card title="Current Utilisation">
        {normalised ? (
          <PercentageChart cohorts={cohorts} />
        ) : (
          <MoneyChart cohorts={cohorts} currency={currency} tokenCurrency={tokenCurrency} />
        )}
      </Card>
      <Box css={{ height: 24 }} />
      <Card title="History">
        <UtilisationHistoryByCohort
          countryCode={countryCode}
          supporterId={supporterId}
          excludeGivenwell={excludeGivenwell}
        />
      </Card>
    </Box>
  );
}

type PercentageChartProps = {
  cohorts: CohortSummary[];
};
function PercentageChart({ cohorts }: PercentageChartProps) {
  const height = AXIS_HEIGHT + cohorts.length * BAR_HEIGHT + (cohorts.length - 1) * BAR_GAP;

  const definition = useMemo(() => {
    const rulers = range(0, 101, 25);
    const labels = cohorts.map(describeCohort);

    return defineChart({
      marks: [
        // Manual gridlines at each ruler value.
        ruleX(rulers, { stroke: "lightgray", strokeWidth: 1, strokeOpacity: 1 }),

        barX(cohorts, {
          id: "utilisation",
          x1: 0,
          x2: cohortUtilisation,
          y: describeCohort,
          fill: "steelblue",
        }),

        // Show labels for the utilisation %.
        text(cohorts, {
          x: cohortUtilisation,
          y: describeCohort,
          text: cohort => `${cohortUtilisation(cohort).toFixed(0)}%`,
          fill: "white",
          fontSize: 10,
          anchor: "end",
          dx: -8,
        }),
      ],
      scales: {
        x: {
          scale: scaleLinear().domain([0, 100]),
          axis: {
            ticks: { values: rulers, size: 2, format: value => `${value}%` },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
        y: {
          scale: scaleBand<string>().domain(labels).padding(0.1).round(true),
          grid: false,
          axis: {
            ticks: { size: 10 },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
      },
      margin: { top: 20, left: 80, right: 40 },
    });
  }, [cohorts]);

  return (
    <ChartBox>
      <Chart definition={definition} width={400} height={height} ariaLabel="Current utilisation by cohort" />
    </ChartBox>
  );
}

type MoneyChartProps = {
  cohorts: CohortSummary[];
  // Both unset for the combined all-countries view, which mixes token currencies and so
  // has no meaningful single-currency conversion — the chart shows raw token counts instead.
  currency?: string;
  tokenCurrency?: TokenCurrencyCode;
};
function MoneyChart({ cohorts, currency, tokenCurrency }: MoneyChartProps) {
  const height = AXIS_HEIGHT + cohorts.length * BAR_HEIGHT + (cohorts.length - 1) * BAR_GAP;

  const definition = useMemo(() => {
    const tokensToValue = (tokens: number) =>
      tokenCurrency ? tokensToMoney({ amount: tokens, tokenCurrencyCode: tokenCurrency }).amount : tokens;
    const formatValue = (value: number) =>
      currency ? formatDollars(value, currency, { maximumFractionDigits: 0 }) : formatWholeNumber(value);
    const totalValue = (cohort: CohortSummary) => tokensToValue(cohort.total_tokens - cohort.total_returned);
    const spentValue = (cohort: CohortSummary) => tokensToValue(cohort.total_spent);
    // While the query is loading (or every cohort is empty) there is no finite
    // maximum, and the linear scale rejects a non-finite domain — skip the chart.
    const maxValue = tokensToValue(Math.max(0, ...cohorts.map(c => c.total_tokens)));
    if (!Number.isFinite(maxValue) || maxValue <= 0) return null;
    const spacing = getNiceSpacing(maxValue);
    const valueLimit = Math.ceil(maxValue / spacing) * spacing; // Round up to the nearest 10,000.
    const rulers = range(0, valueLimit + spacing, spacing);
    const minimumLabelOffset = valueLimit / 30;
    const labels = cohorts.map(describeCohort);

    return defineChart({
      marks: [
        // Manual gridlines at each ruler value.
        ruleX(rulers, { stroke: "lightgray", strokeWidth: 1, strokeOpacity: 1 }),

        // Total bar. Explicit x1/x2 endpoints keep the two bar marks overlaid
        // on the same band instead of stacking.
        barX(cohorts, {
          id: "total",
          x1: 0,
          x2: totalValue,
          y: describeCohort,
          fill: "steelblue",
        }),

        // Spent bar.
        barX(cohorts, {
          id: "spent",
          x1: 0,
          x2: spentValue,
          y: describeCohort,
          fill: "rgba(0,0,0,0.55)",
        }),

        // Utilised % label.
        text(cohorts, {
          x: cohort => Math.max(minimumLabelOffset, spentValue(cohort)),
          y: describeCohort,
          text: cohort => `${cohortUtilisation(cohort).toFixed(0)}%`,
          fill: "white",
          fontSize: 10,
          anchor: "end",
          dx: -8,
        }),

        // Total $ label.
        text(cohorts, {
          x: totalValue,
          y: describeCohort,
          text: cohort => formatValue(totalValue(cohort)),
          fill: "grey",
          fontSize: 10,
          anchor: "start",
          dx: 8,
        }),
      ],
      scales: {
        x: {
          scale: scaleLinear().domain([0, valueLimit]),
          axis: {
            ticks: { values: rulers, size: 2, format: formatValue },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
        y: {
          scale: scaleBand<string>().domain(labels).padding(0.1).round(true),
          grid: false,
          axis: {
            ticks: { size: 10 },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
      },
      margin: { top: 20, left: 80, right: 40 },
      pointer: false,
    });
  }, [cohorts, currency, tokenCurrency]);

  return (
    <ChartBox>
      {definition && <Chart definition={definition} height={height} ariaLabel="Current utilisation by cohort" />}
    </ChartBox>
  );
}

type CohortRangePickerProps = {
  from: Date;
  to: Date;
  onChange: (from: Date, to: Date) => void;
};
function CohortRangePicker(props: CohortRangePickerProps) {
  return (
    <Flex css={{ gap: 4, items: "center" }}>
      <MonthPicker value={props.from} onValueChange={date => props.onChange(date, props.to)} />–
      <MonthPicker value={props.to} onValueChange={date => props.onChange(props.from, date)} />
    </Flex>
  );
}

function cohortUtilisation(cohort: CohortSummary): number {
  return (cohort.total_spent / (cohort.total_tokens - cohort.total_returned)) * 100;
}

function describeCohort(cohort: CohortSummary) {
  const date = new Date(cohort.cohort_date);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function range(start: number, stop: number, step: number) {
  return Array.from({ length: Math.ceil((stop - start) / step) }, (_, i) => start + i * step);
}

function getNiceSpacing(maxDollars: number): number {
  const targetTickCount = 5; // you can tune this to your needs (e.g. 4–7)
  const rawSpacing = maxDollars / targetTickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawSpacing)));
  const residual = rawSpacing / magnitude;

  if (residual >= 5) return 10 * magnitude;
  if (residual >= 2) return 5 * magnitude;
  return 2 * magnitude;
}
